import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Grid, Container, Typography, Box, Card, CardContent,
    Chip, Fade, Divider
} from "@mui/material";
import {
    SportsEsports, PlayArrow, Gamepad,
    Casino, VideogameAsset
} from "@mui/icons-material";

import NavBar from '../../Components/NavBar/navbar';
import FooterSection from '../../Components/Footer/footer';
import { gamesData } from '../../Components/Arcade/gamesConfig';
import DashboardHome from './Dashboard/DashboardHome';
import ArcadeHubNav, {
    ARCADE_SECTION_PARAM,
    ARCADE_SECTIONS,
} from './ArcadeHubNav';
import usePageTitle from '../../Components/Arcade/ui/usePageTitle';
import { COLOR, GAME_ACCENT } from '../../Components/Arcade/ui/tokens';
import { difficultyChipSx } from '../../Components/Arcade/ui/difficultyChip';

const Arcade = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const sectionParam = searchParams.get(ARCADE_SECTION_PARAM);
    const section = sectionParam === ARCADE_SECTIONS.games
        ? ARCADE_SECTIONS.games
        : ARCADE_SECTIONS.dashboard;
    usePageTitle(section === ARCADE_SECTIONS.games ? 'Games' : 'Dashboard');

    const handleGameSelect = (gameId) => {
        navigate(`/arcade/${gameId}`);
    };


    const gameAccent = (game) => GAME_ACCENT[game.backendId] || game.color || COLOR.primary;

    return (
        <Box sx={{ bgcolor: '#0a0a0a', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
            <NavBar />

            {/* Main Content */}
            <Box sx={{ flexGrow: 1, pt: 12, pb: 8 }}>
                <Container maxWidth="lg">

                    {/* Header Section */}
                    <Box sx={{ textAlign: 'center', mb: 8 }}>
                        <Fade in={true} timeout={1000}>
                            <Box>
                                <Box sx={{
                                    display: 'inline-flex',
                                    p: 2,
                                    borderRadius: '50%',
                                    bgcolor: 'rgba(44, 187, 93, 0.1)',
                                    mb: 3
                                }}>
                                    <SportsEsports sx={{ fontSize: 40, color: '#2cbb5d' }} />
                                </Box>
                                <Typography component="h1" variant="h2" sx={{
                                    color: COLOR.text,
                                    fontWeight: 800,
                                    mb: 2,
                                    letterSpacing: '-1px'
                                }}>
                                    Gaming <span style={{ color: COLOR.primary }}>Arcade</span>
                                </Typography>
                                <Typography variant="h6" sx={{ color: COLOR.textMuted, maxWidth: '600px', mx: 'auto', lineHeight: 1.6 }}>
                                    Pick a game, upload an agent, and see how it plays.
                                </Typography>
                            </Box>
                        </Fade>
                    </Box>

                    <ArcadeHubNav activeSection={section} />

                    {section === ARCADE_SECTIONS.dashboard && (
                        <Fade in={true} timeout={500}>
                            <Box sx={{ mb: 8 }}>
                                <DashboardHome embedded />
                            </Box>
                        </Fade>
                    )}

                    {section === ARCADE_SECTIONS.games && (
                    <>
                    {/* Games Grid */}
                    <Grid container spacing={{ xs: 1.5, sm: 3 }}>
                        {gamesData.map((game, index) => (
                            <Grid item xs={6} md={6} lg={4} key={game.id}>
                                <Fade in={true} timeout={500 + (index * 100)}>
                                    <Card
                                        onClick={() => handleGameSelect(game.id)}
                                        sx={{
                                            bgcolor: COLOR.surface,
                                            border: `1px solid ${COLOR.border}`,
                                            borderRadius: '16px',
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            cursor: 'pointer',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            '&:hover': {
                                                transform: 'translateY(-8px)',
                                                borderColor: gameAccent(game),
                                                boxShadow: `0 12px 40px -10px ${gameAccent(game)}40`,
                                                '& .game-icon': {
                                                    color: gameAccent(game),
                                                    transform: 'scale(1.1) rotate(5deg)'
                                                },
                                                '& .play-button': {
                                                    opacity: 1,
                                                    transform: 'translateX(0)'
                                                }
                                            }
                                        }}
                                    >
                                        {/* Top Color Bar */}
                                        <Box sx={{ height: '4px', bgcolor: gameAccent(game), opacity: 0.8 }} />

                                        <CardContent sx={{ p: { xs: 1.5, sm: 4 }, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: { xs: 1, sm: 3 } }}>
                                                <Box className="game-icon" sx={{
                                                    color: COLOR.textSubtle,
                                                    transition: 'all 0.3s ease',
                                                    bgcolor: 'rgba(255,255,255,0.03)',
                                                    p: 1.5,
                                                    borderRadius: '12px'
                                                }}>
                                                    <VideogameAsset fontSize="large" />
                                                </Box>
                                                <Chip
                                                    label={game.difficulty}
                                                    size="small"
                                                    sx={difficultyChipSx(game.difficulty)}
                                                />
                                            </Box>

                                            <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', mb: 1, fontSize: { xs: '1rem', sm: '1.5rem' } }}>
                                                {game.title}
                                            </Typography>

                                            <Typography variant="body2" sx={{ color: COLOR.textMuted, mb: 3, flexGrow: 1, display: { xs: 'none', sm: 'block' } }}>
                                                {game.description}
                                            </Typography>

                                            <Divider sx={{ borderColor: COLOR.border, mb: 2, display: { xs: 'none', sm: 'block' } }} />

                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                <Typography variant="caption" sx={{ color: COLOR.textMuted, fontWeight: 600 }}>
                                                    {game.genre}
                                                </Typography>
                                                <Box className="play-button" sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    color: gameAccent(game),
                                                    fontWeight: 'bold',
                                                    opacity: 0.85,
                                                    transform: 'translateX(-5px)',
                                                    transition: 'all 0.3s ease'
                                                }}>
                                                    Play <PlayArrow sx={{ ml: 0.5, fontSize: 18 }} />
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                </Fade>
                            </Grid>
                        ))}
                    </Grid>

                    {/* Features Section */}
                    <Box sx={{ mt: 16 }}>
                        <Typography variant="overline" sx={{ color: COLOR.primary, fontWeight: 'bold', display: 'block', textAlign: 'center', mb: 1 }}>
                            Why Arcade
                        </Typography>
                        <Typography variant="h3" sx={{ color: 'white', fontWeight: 800, textAlign: 'center', mb: 6 }}>
                            What you get
                        </Typography>

                        <Grid container spacing={4} justifyContent="center">
                            {[
                                { icon: <Casino />, title: 'Live state', desc: 'Tweak state and reload agents without restarting.' },
                                { icon: <Gamepad />, title: 'Varied games', desc: 'From mazes and warehouses to classic arcade fare.' },
                                { icon: <SportsEsports />, title: 'Fast rendering', desc: 'WebGL that stays smooth with busy boards.' }
                            ].map((feature, index) => (
                                <Grid item xs={12} md={4} key={index}>
                                    <Box sx={{
                                        p: 4,
                                        bgcolor: '#111',
                                        borderRadius: '16px',
                                        border: '1px solid #222',
                                        height: '100%',
                                        transition: 'border-color 0.2s',
                                        '&:hover': { borderColor: '#444' }
                                    }}>
                                        <Box sx={{ color: COLOR.primary, mb: 2 }}>
                                            {React.cloneElement(feature.icon, { fontSize: "large" })}
                                        </Box>
                                        <Typography variant="h6" sx={{ color: 'white', mb: 1, fontWeight: 'bold' }}>
                                            {feature.title}
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: COLOR.textMuted, lineHeight: 1.6 }}>
                                            {feature.desc}
                                        </Typography>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                    </>
                    )}
                </Container>
            </Box>

            <FooterSection />
        </Box>
    );
}

export default Arcade;
